'use client';

import { useState } from 'react';

/**
 * The Overview switch, in the language bar above the pane it affects.
 *
 * Every answer arrives with a one-sentence summary of itself; this decides
 * whether the pane draws it, in a card above the translation. It is a show/hide
 * and nothing more — pressing it never asks the model anything, so it answers at
 * the speed of a checkbox rather than of a translation.
 *
 * It lives here rather than in the mode column because it is not a mode: modes
 * are mutually exclusive answers to "what should this text become", and this is
 * a yes/no that composes with every one of them. Putting it in that column would
 * have made it look like a seventh mode that turns the others off.
 *
 * The styling deliberately copies ModeColumn — same border, same radius, same
 * 120ms lift, same tooltip card — because it sits eight pixels away from those
 * buttons and anything that looked *nearly* like them would read as a mistake.
 */
export default function OverviewToggle({
  on,
  attention,
  onToggle,
}: {
  on: boolean;
  /**
   * Draw the eye to it: a fresh translation has just landed, the switch is off,
   * and this visitor has never pressed it. See `loadOverviewSeen`.
   */
  attention: boolean;
  onToggle: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      // The group in LanguageTabs owns the `ml-auto` that pushes this pair to
      // the right edge; this one only needs to anchor its own tooltip.
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/*
        The sparkles.

        Four small stars on their own staggered loop rather than one synchronised
        flash — things that twinkle in nature are never in step, and four points
        blinking together reads as an error badge instead of an invitation.

        `pointer-events-none` matters: they overhang the button on every side,
        and a star swallowing the click would make the control feel broken at the
        exact moment someone finally tried it.
      */}
      {attention && (
        <span className="pointer-events-none absolute -inset-2 z-0" aria-hidden>
          {[
            { top: '-2px', left: '-1px', size: 9, delay: '0s' },
            { top: '4px', right: '-3px', size: 7, delay: '0.45s' },
            { bottom: '-1px', left: '6px', size: 6, delay: '0.9s' },
            { bottom: '5px', right: '2px', size: 8, delay: '1.25s' },
          ].map((s, i) => (
            <svg
              key={i}
              viewBox="0 0 24 24"
              className="gt-spark absolute text-amber-400"
              style={{ ...s, width: s.size, height: s.size, animationDelay: s.delay }}
              fill="currentColor"
            >
              {/* A four-point sparkle, not a five-point star: at 7px a star is a
                  blob, while the concave arms of this one still read. */}
              <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
            </svg>
          ))}
        </span>
      )}

      <button
        type="button"
        aria-pressed={on}
        aria-label="Overview. Show a one-sentence summary above the translation."
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={onToggle}
        className={
          'relative z-10 flex h-9 w-9 items-center justify-center rounded-lg border ' +
          'transition-all duration-[120ms] ease-out hover:-translate-y-0.5 hover:shadow-md ' +
          'active:translate-y-0 active:scale-95 motion-reduce:transform-none ' +
          (attention ? 'gt-attn ' : '') +
          (on
            ? 'border-accent-500 bg-accent-600 text-white shadow-sm hover:bg-accent-700'
            : 'border-transparent text-ink-900 hover:border-ink-200 hover:bg-ink-100 ' +
              'hover:text-accent-600 dark:text-ink-dim dark:hover:border-ink-700 ' +
              'dark:hover:bg-ink-800 dark:hover:text-accent-400')
        }
      >
        {/* The bolt is filled when the switch is on and outlined when it is off,
            so the state survives being seen in a photograph of the page — colour
            alone would not, and it is the only thing distinguishing the two. */}
        <svg
          viewBox="0 0 24 24"
          className="h-[18px] w-[18px]"
          fill={on ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={on ? 1 : 1.7}
          strokeLinejoin="round"
        >
          <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12z" />
        </svg>
      </button>

      {hovered && (
        <div
          role="tooltip"
          // Right-aligned: this button sits at the right edge of the bar, and a
          // centred tooltip would hang off the card.
          className="pointer-events-none absolute right-0 top-full z-40 mt-1.5 w-60 rounded-lg
                     border border-ink-200 bg-white p-2.5 text-left shadow-xl
                     dark:border-ink-700 dark:bg-ink-800"
        >
          <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Overview</p>
          <p className="mt-1 text-xs leading-relaxed text-ink-500 dark:text-ink-300">
            Put a one-sentence summary of the translation above it, or leave the
            translation on its own.
          </p>
        </div>
      )}
    </div>
  );
}
